import React, { createContext, useContext, useState, useEffect } from 'react';
import { translations, PublicTranslations } from '../i18n/publicTranslations';

type Language = 'en' | 'es' | 'pt';

interface LanguageContextType {
  language: Language;
  setLanguage: (lang: Language) => void;
  t: PublicTranslations;
}

const LanguageContext = createContext<LanguageContextType | undefined>(undefined);

export const LanguageProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [language, setLanguageState] = useState<Language>(() => {
    // A /es/... or /pt/... URL is an explicit request for that language and
    // outranks whatever was stored last visit — it is also what search engines
    // index, so the page has to match the URL they crawled.
    if (typeof window !== 'undefined') {
      const prefixed = /^\/(es|pt)(\/|$)/.exec(window.location.pathname);
      if (prefixed) return prefixed[1] as Language;
    }
    const saved = localStorage.getItem('publicLanguage');
    return (saved === 'es' || saved === 'pt' ? saved : 'en') as Language;
  });

  useEffect(() => {
    localStorage.setItem('publicLanguage', language);
  }, [language]);

  const setLanguage = (lang: Language) => {
    setLanguageState(lang);
  };

  const value: LanguageContextType = {
    language,
    setLanguage,
    t: translations[language],
  };

  return (
    <LanguageContext.Provider value={value}>
      {children}
    </LanguageContext.Provider>
  );
};

/**
 * Picks a string by the current public language, for the one-off marketing copy
 * that never made it into publicTranslations. English is the fallback, so a
 * language with no translation for a given string still reads.
 */
export const usePublicTr = () => {
  const { language } = usePublicLanguage();
  return (en: string, es: string, pt?: string): string => {
    if (language === 'es') return es;
    if (language === 'pt') return pt ?? en;
    return en;
  };
};

export const usePublicLanguage = () => {
  const context = useContext(LanguageContext);
  if (context === undefined) {
    throw new Error('usePublicLanguage must be used within a LanguageProvider');
  }
  return context;
};
